import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import './SignInGate.css';

/**
 * Shown once a band has finished rendering, if the visitor has no account.
 *
 * Deliberately placed AFTER the reveal rather than before the calculation. The
 * band is visible behind the panel, so the gate asks someone to keep something
 * they can already see rather than to buy a promise.
 *
 * ALLOW_SKIP controls whether it is a hard gate or a prompt:
 *   false — no account, no band. Highest signup rate, highest drop-off.
 *   true  — "not now" lets them look, and they can sign in later.
 * Note that with ALLOW_SKIP false the landing page's claim that calculating is
 * free is no longer strictly true, so that copy needs revisiting alongside it.
 */
const ALLOW_SKIP = true;

export default function SignInGate({ open, onSkip }) {
  const { enabled, user, renderButton, gsiReady } = useAuth();
  const slotRef = useRef(null);

  useEffect(() => {
    if (!open || user || !gsiReady) return;
    renderButton(slotRef.current);
  }, [open, user, gsiReady, renderButton]);

  // Stop the page scrolling behind the panel while it is up.
  useEffect(() => {
    if (!open || user) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, user]);

  if (!enabled || !open || user) return null;

  return (
    <div className="sg" role="dialog" aria-modal="true" aria-labelledby="sg-title">
      <div className="sg-scrim" />

      <div className="sg-panel">
        <span className="sg-eyebrow">Your band is ready</span>

        <h2 className="sg-title" id="sg-title">Keep it</h2>

        <p className="sg-body">
          This band exists only in this browser tab right now. Sign in and it is saved
          to your account — on this device and any other.
        </p>

        <div className="sg-button" ref={slotRef} />
        {!gsiReady && <p className="sg-loading">Loading sign-in&hellip;</p>}

        {ALLOW_SKIP && (
          <button className="sg-skip" onClick={onSkip}>
            Not now — just show me the band
          </button>
        )}

        <p className="sg-foot">
          No account is needed to buy a print. Signing in only keeps your bands together.
        </p>
      </div>
    </div>
  );
}
